/**
 * Trip CO2 Calculator — map hero.
 *
 * One straight, dashed line between origin and destination on a
 * Mapbox base, labelled with the straight-line (≈ haversine)
 * distance. The dashed style reads as "as the crow flies" — an
 * approximate visual for the trip rather than a routed path.
 *
 * Why React `<Marker>` for the label rather than a Mapbox `symbol`
 * layer: the distance label is rendered as HTML so we can use real
 * CSS for the white halo + colour. Mapbox's symbol-layer text would
 * also work for a pure-text label, but the HTML approach keeps the
 * rendering pipeline consistent with the city Pins.
 *
 * Reuses `MAPBOX_TOKEN`, `Pin`, and `MissingTokenPlaceholder` from
 * the shared map primitives.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Map, { Layer, Marker, Source, type MapRef } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { LatLng } from '../../entities/geo/types';
import { MAPBOX_TOKEN, MissingTokenPlaceholder, Pin } from '../maps/mapPrimitives';

// ─────────────────────────────────────────────────────────────────────────────
// Tuning
// ─────────────────────────────────────────────────────────────────────────────

const LINE_COLOR = '#475569'; // slate-600 — neutral, doesn't fight the map tiles
const FROM_COLOR = '#10b981';
const TO_COLOR = '#ef4444';

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export interface MapHeroProps {
  origin: LatLng;
  destination: LatLng;
  /** Origin display name shown on the From-pin (e.g. "Berlin"). */
  originName: string;
  /** Destination display name shown on the To-pin (e.g. "Barcelona"). */
  destinationName: string;
  height?: number;
  /**
   * Extra top padding (px) to add when fitting bounds, so callers who
   * overlay UI on the map's top edge (e.g. the trip-summary pills)
   * can shift the route downward by an equivalent amount and keep it
   * out from under the overlay. Added to the baseline top padding,
   * not replacing it.
   */
  topReserve?: number;
}

const MapHero: React.FC<MapHeroProps> = ({
  origin,
  destination,
  originName,
  destinationName,
  height = 240,
  topReserve = 0,
}) => {
  const mapRef = useRef<MapRef | null>(null);
  /**
   * fitBounds called *before* the map's style finishes loading can
   * silently misfire — the camera lands somewhere between the
   * initial-view-state and the desired bounds rather than at the
   * target zoom. The effect ran every time origin/destination
   * changed but the FIRST run (on mount) was racing the Mapbox
   * init. Gating on `onLoad` removes the race; the user-visible
   * symptom was "long trips like Berlin↔Bangkok don't show the
   * whole route, just the midpoint".
   */
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  // Distances — both derived from a single haversine call so the
  // multipliers stay easy to update in one place.
  const straightKm = useMemo(() => haversineKm(origin, destination), [origin, destination]);
  // Straight-line geometry between the two endpoints.
  const lineCoords = useMemo<Array<[number, number]>>(
    () => [
      [origin.lng, origin.lat],
      [destination.lng, destination.lat],
    ],
    [origin, destination]
  );

  const lineGeo = useMemo(() => makeLineFeature(lineCoords), [lineCoords]);

  // Distance label sits at the midpoint of the line and is rotated
  // to follow the line's direction. The midpoint is a safe default
  // now that there's only one label to place; no separation logic
  // needed.
  const labelAngleDeg = lineAngleDeg(origin, destination);
  const labelPos: [number, number] = [
    (origin.lng + destination.lng) / 2,
    (origin.lat + destination.lat) / 2,
  ];

  // Auto-fit on origin/destination change. Waits for `isMapLoaded`
  // so the first fit doesn't race the Mapbox style/tiles init.
  useEffect(() => {
    if (!isMapLoaded) return;
    const map = mapRef.current;
    if (!map) return;
    if (!isFinite(origin.lat) || !isFinite(origin.lng)) return;
    if (!isFinite(destination.lat) || !isFinite(destination.lng)) return;

    // Straight line: the bbox is just the two endpoints.
    const minLng = Math.min(origin.lng, destination.lng);
    const maxLng = Math.max(origin.lng, destination.lng);
    const minLat = Math.min(origin.lat, destination.lat);
    const maxLat = Math.max(origin.lat, destination.lat);

    map.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      {
        // Tight padding so the route fills more of the viewport. The
        // base values are deliberately small — the City `Pin` markers
        // anchor at the bottom of their label, so a tight bbox still
        // leaves them visible inside the map (the pin label rises ~20px
        // above the geographic point). The asymmetry — more top than
        // bottom — biases the route a touch south of geometric centre
        // so the visual weight of the line sits below the eye-line
        // rather than dead-centre on tall map tiles. `topReserve`
        // stacks on top when the trip-summary overlay is showing.
        padding: {
          top: 48 + topReserve,
          right: 28,
          bottom: 24,
          left: 28,
        },
        duration: 600,
        maxZoom: 10,
      }
    );
  }, [origin, destination, isMapLoaded, topReserve]);

  if (!MAPBOX_TOKEN) return <MissingTokenPlaceholder height={height} />;

  const initialCenter: [number, number] = [
    (origin.lng + destination.lng) / 2,
    (origin.lat + destination.lat) / 2,
  ];

  return (
    <div style={{ height, borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{ longitude: initialCenter[0], latitude: initialCenter[1], zoom: 3 }}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        style={{ width: '100%', height: '100%' }}
        // Page scroll passes through cleanly; pan + pinch-zoom on
        // touch still work if the user wants to look around.
        scrollZoom={false}
        doubleClickZoom={false}
        // Signal that the underlying Mapbox instance is ready for
        // imperative camera calls (fitBounds). The effect above
        // waits for this so the first fit always lands correctly.
        onLoad={() => setIsMapLoaded(true)}
      >
        {/* Straight, dashed line between the two endpoints. */}
        <Source id="trip-line" type="geojson" data={lineGeo}>
          <Layer
            id="trip-line-stroke"
            type="line"
            layout={{ 'line-cap': 'round', 'line-join': 'round' }}
            paint={{ 'line-color': LINE_COLOR, 'line-width': 2.5, 'line-dasharray': [2, 1.5] }}
          />
        </Source>

        {/* Distance label at the line's midpoint, rotated to follow
            the line direction. HTML marker rather than a Mapbox
            symbol layer so the CSS halo + colour stay easy to tune. */}
        <Marker longitude={labelPos[0]} latitude={labelPos[1]} anchor="center">
          <DistanceChip
            km={straightKm}
            color={LINE_COLOR}
            rotateDeg={labelAngleDeg}
          />
        </Marker>

        {/* Endpoint pins use the actual city names so the user always
            knows which side is which without reading the inputs panel. */}
        <Marker longitude={origin.lng} latitude={origin.lat} anchor="bottom">
          <Pin color={FROM_COLOR} label={originName} variant="text" />
        </Marker>
        <Marker longitude={destination.lng} latitude={destination.lat} anchor="bottom">
          <Pin color={TO_COLOR} label={destinationName} variant="text" />
        </Marker>
      </Map>
    </div>
  );
};

export default MapHero;

// ─────────────────────────────────────────────────────────────────────────────
// Distance label chip (HTML, sits on top of the map)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Subtle line label: colored text with a soft white halo, no pill
 * background. Mimics Mapbox's native `text-color + text-halo-color`
 * symbol-layer look (which we can't use directly because Mapbox's
 * glyph fonts don't include emoji ranges). The multi-shadow stack
 * builds an even halo around the glyphs.
 */
const DistanceChip: React.FC<{
  km: number;
  color: string;
  /** Degrees to rotate the chip so it visually follows its line. */
  rotateDeg?: number;
}> = ({ km, color, rotateDeg = 0 }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      color,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.02,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      textShadow:
        '0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff, 0 0 5px #fff',
      // The chip is rotated around its own center (the Marker's
      // anchor="center" handles geographical positioning; the rotate
      // affects the visual orientation of the text).
      transform: `rotate(${rotateDeg.toFixed(2)}deg)`,
      transformOrigin: '50% 50%',
    }}
  >
    {Math.round(km).toLocaleString()} km
  </span>
);

// ─────────────────────────────────────────────────────────────────────────────
// Geometry helpers
// ─────────────────────────────────────────────────────────────────────────────

function makeLineFeature(coords: Array<[number, number]>) {
  return {
    type: 'FeatureCollection' as const,
    features: [
      {
        type: 'Feature' as const,
        properties: {},
        geometry: { type: 'LineString' as const, coordinates: coords },
      },
    ],
  };
}

/**
 * Approximate angle of the origin→destination line, in degrees,
 * for CSS `transform: rotate(...)`. Computed in lat/lng space with
 * latitude flipped (north is up on screen, but +Y is down in CSS),
 * then clamped to `[-90°, 90°]` so the rotated text never reads
 * upside-down.
 *
 * Lat/lng-space angle corrected for the Web-Mercator projection so
 * the label visibly stays parallel to the line at any latitude. In
 * Web-Mercator a degree of latitude maps to ~1/cos(φ) more vertical
 * pixels than a degree of longitude, so we scale the latitude delta
 * by 1/cos(mean latitude) before taking atan2. Without this the
 * label drifts noticeably flatter than the line at European
 * latitudes (φ ≈ 50°, scale ≈ 1.56).
 *
 * Still approximate at very-large-bbox trips because the scale is
 * computed at the midpoint rather than integrated along the line,
 * but for trip-sized distances the residual error is well below
 * what the eye can detect.
 */
function lineAngleDeg(from: LatLng, to: LatLng): number {
  const dLng = to.lng - from.lng;
  const dLat = to.lat - from.lat;
  const meanLatRad = ((from.lat + to.lat) / 2) * (Math.PI / 180);
  const latStretch = 1 / Math.cos(meanLatRad);
  // atan2(screen_dy, screen_dx). screen_dy = -dLat·stretch because
  // higher latitude is *higher* on screen but CSS +Y is *down*, and
  // Mercator stretches latitude by 1/cos(φ).
  const rad = Math.atan2(-dLat * latStretch, dLng);
  let deg = (rad * 180) / Math.PI;
  if (deg > 90) deg -= 180;
  if (deg < -90) deg += 180;
  return deg;
}

/**
 * Haversine great-circle distance in kilometres between two lat/lng
 * points. Mirrors `backend/services/carbonAccounting/haversine.js`
 * (kept inline here because Phase 4's wire format never sends the
 * raw straight-line distance — only the detour-adjusted one).
 */
function haversineKm(a: LatLng, b: LatLng): number {
  const R = 6371; // Earth radius in km
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h = sinDLat * sinDLat + sinDLng * sinDLng * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return R * c;
}
